import { Dictionary } from "../../../entities/common";
import { CalculatedField, FieldGroup, FieldType, FormatType } from "../../../entities/Metadata";
import { toDate } from "../../utils/common";
import { TimeTypesConfig } from "./ResourceUsageCell/common";

export const assignmentTimeTypesConfigMap: TimeTypesConfig = {
    plan: { type: "plan", title: "Effort", className: "plan" },
    actual: { type: "actual", title: "Completed Work", className: "actual" },
    remaining: { type: "remaining", title: "Remaining Work", className: "remaining" },
    availability: { type: "availability", title: "Availability", className: "availability" },
    capacity: { type: "capacity", title: "Capacity", className: "capacity" }
};

export type AssignmentsWork = {
    effort: number;
    completedWork: number;
    remainingWork: number;
};

export type ProjectAssignmentsWork = AssignmentsWork & {
    resourceEffort: number;
    otherProjectsEffort: Dictionary<number>;
};

export type AssignmentsTotals = AssignmentsWork & {
    estimatedCharge: number;
    estimatedCost: number;
    actualCharge: number;
    actualCost: number;
};

export type ProjectAssignments = {
    byDay: Dictionary<ProjectAssignmentsWork>;
    totals: AssignmentsTotals;
};

export type ServerAssignmentsByDay = AssignmentsWork & {
    date: string;
};

export type ServerProjectAssignments = {
    days: (ProjectAssignmentsWork & { date: string })[];
    totals: AssignmentsTotals;
};

export const toAssignmentsByDay = (byDay: ServerAssignmentsByDay[]): Dictionary<AssignmentsWork> =>
    byDay.reduce<Dictionary<AssignmentsWork>>((acc, _) => {
        acc[toDate(_.date)!.getBeginOfDay().toISOString()] = {
            effort: _.effort,
            completedWork: _.completedWork,
            remainingWork: _.remainingWork
        };
        return acc;
    }, {});

export const toProjectAssignments = (assignments: ServerProjectAssignments): ProjectAssignments => ({
    byDay: assignments.days.reduce<Dictionary<ProjectAssignmentsWork>>((acc, _) => {
        acc[toDate(_.date)!.getBeginOfDay().toISOString()] = {
            effort: _.effort,
            completedWork: _.completedWork,
            remainingWork: _.remainingWork,
            resourceEffort: _.resourceEffort,
            otherProjectsEffort: _.otherProjectsEffort
        };
        return acc;
    }, {}),
    totals: assignments.totals
});

type AssignmentsTotalColumn = { id: string; label: string; format: FormatType; value: (totals: AssignmentsTotals) => number };

const assignmentsTotalColumns: AssignmentsTotalColumn[] = [
    { id: "19422434-d128-4d74-934c-01b5ec36a366", label: "Total Effort", format: FormatType.Duration, value: _ => _.effort },
    { id: "d21533ac-5610-4207-bc86-9e427565e46c", label: "Total Completed", format: FormatType.Duration, value: _ => _.completedWork },
    { id: "20cd3840-43a3-4841-90f9-aa9d9972b1b7", label: "Total Remaining", format: FormatType.Duration, value: _ => _.remainingWork },
    { id: "6f0d2b8e-3c41-4a7e-9b52-8e1f4d6a2c93", label: "Estimated Charge", format: FormatType.Cost, value: _ => _.estimatedCharge },
    { id: "b7e3a9d1-5f28-4c6b-a0e4-2d9c7f1b8e56", label: "Estimated Cost", format: FormatType.Cost, value: _ => _.estimatedCost },
    { id: "3a8c5e2f-9d14-4b7a-8c63-f1e0b4d7a925", label: "Actual Charge", format: FormatType.Cost, value: _ => _.actualCharge },
    { id: "e94d1b6c-2a7f-4e85-b3d0-7c5a9f2e1d48", label: "Actual Cost", format: FormatType.Cost, value: _ => _.actualCost }
];

export const buildAssignmentsTotalFields = (
    buildCalculate: (value: (totals: AssignmentsTotals) => number) => CalculatedField["calculate"]
): CalculatedField[] =>
    assignmentsTotalColumns.map(_ => ({
        id: _.id,
        type: FieldType.Decimal,
        name: _.id,
        label: _.label,
        isNative: true,
        isCustom: false,
        isReadonly: true,
        isSystem: true,
        group: FieldGroup.SystemFields,
        settings: { format: _.format },
        calculate: buildCalculate(_.value)
    }));
